import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import clsx from 'clsx';

const MAX_CHARS = 2000;
const CHAR_WARNING_THRESHOLD = 1800;

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export const ChatInput = ({ onSend, disabled = false }: ChatInputProps) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;

    onSend(trimmed);
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, disabled, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_CHARS) {
      setValue(newValue);
    }

    // Auto-resize
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const remaining = MAX_CHARS - value.length;
  const showCounter = remaining <= MAX_CHARS - CHAR_WARNING_THRESHOLD;

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="Ask about invoices, approvals, vendors..."
          rows={1}
          className={clsx(
            'flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100',
            'px-3 py-2 text-sm',
            'placeholder-gray-500 dark:placeholder-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary-rgb))]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors'
          )}
        />
        <button
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          className={clsx(
            'p-2 rounded-lg transition-colors',
            value.trim() && !disabled
              ? 'bg-[rgb(var(--color-primary-rgb))] text-white hover:opacity-90'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
          )}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
      {showCounter && (
        <p
          className={clsx(
            'text-xs mt-1 text-right',
            remaining < 50 ? 'text-red-500' : 'text-gray-400'
          )}
        >
          {remaining} characters remaining
        </p>
      )}
    </div>
  );
};
